type HeightRef = (node: HTMLElement | null) => (() => void) | undefined;

const refs = new Map<string, HeightRef>();

/**
 * A ref that keeps a CSS variable on the element's parent equal to the
 * element's height, so content below a sticky bar can stick just under it.
 * With `base`, the value is that variable plus the height, for stacking a
 * second sticky bar under a first one. The same arguments return the same
 * ref, so React does not re-attach it on every render.
 */
export const stickyHeightRef = (name: string, base?: string): HeightRef => {
  const key = `${name}|${base ?? ''}`;
  const cached = refs.get(key);
  if (cached) {
    return cached;
  }
  const ref: HeightRef = (node) => {
    const target = node?.parentElement;
    if (!node || !target || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const publish = () => {
      const height = `${node.offsetHeight}px`;
      target.style.setProperty(
        name,
        base ? `calc(var(${base}, 0px) + ${height})` : height,
      );
    };
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(node);
    return () => {
      observer.disconnect();
      target.style.removeProperty(name);
    };
  };
  refs.set(key, ref);
  return ref;
};
